import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

export const sessionEndedMessage = msg`Your session ended. Sign in again.`;

const microsoftErrors: Record<string, MessageDescriptor> = {
  email_conflict: msg`This email already belongs to a Borgee account. Continue with password login.`,
  account_deleted: msg`This Microsoft account belongs to a deleted Borgee account.`,
  identity_unnamed: msg`Borgee cannot create an account for this Microsoft account: it has no name or email address to show. Add one to your Microsoft profile, or use password login.`,
  upstream_denied: msg`Microsoft sign-in was cancelled.`,
  upstream_unavailable: msg`Microsoft sign-in is temporarily unavailable. Password login still works.`,
  upstream_invalid_token: msg`Microsoft could not verify this sign-in. Please try again.`,
  upstream_invalid_metadata: msg`Microsoft sign-in is temporarily unavailable. Password login still works.`,
  provider_disabled: msg`Microsoft sign-in is not enabled. Password login still works.`,
  invalid_return_to: msg`This Microsoft sign-in return path is invalid. Please start again.`,
  session_unavailable: msg`Borgee could not create a local session. Please try again.`,
  nonce_mismatch: msg`Microsoft could not verify this sign-in. Please start again.`,
  invalid_flow: msg`This Microsoft sign-in link is invalid or has already been used.`,
  flow_expired: msg`This Microsoft sign-in link expired. Please start again.`,
  database_busy: msg`Microsoft sign-in is temporarily busy. Please try again.`,
  oidc_unavailable: msg`Microsoft sign-in is temporarily unavailable. Password login still works.`,
};

export const microsoftOIDCErrorMessage = (code: string | null): MessageDescriptor | null => {
  if (!code) return null;
  // Callback query input is untrusted; inherited object properties are not error codes.
  return Object.hasOwn(microsoftErrors, code)
    ? microsoftErrors[code]
    : msg`Microsoft sign-in could not be completed. Password login still works.`;
};

export const loginErrorMessage = (status?: number): MessageDescriptor => {
  if (status === 401) return msg`Sign-in was not accepted. Check your credentials or contact an administrator.`;
  if (status === 429) return msg`Too many sign-in attempts. Please try again later.`;
  return msg`Could not sign in. Please try again.`;
};

export const registrationErrorMessage = (status?: number): MessageDescriptor => {
  if (status === 400) return msg`Could not create your account. Check your details and invite code.`;
  if (status === 404) return msg`This invite code is invalid or no longer available. Request a new invite code.`;
  if (status === 409) return msg`An account with this email already exists. Try signing in.`;
  if (status === 429) return msg`Too many registration attempts. Please try again later.`;
  return msg`Could not create your account. Please try again.`;
};
